import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader, AlertCircle } from 'lucide-react';
import FormRenderer from '../../modules/forms/FormRenderer';
import { safeHttpUrl } from '../../utils/safeUrl';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
// A plain axios client, deliberately not the shared authenticated `api`
// service — a public form visitor has no session, and the shared client's
// token-refresh/401 handling has no meaning here.
const publicApi = axios.create({ baseURL: API_BASE_URL });

/**
 * Standalone public route (not inside DashboardLayout) — the same schema
 * that drives the builder's preview later drives this real, submittable
 * page for anonymous visitors, via <FormRenderer />.
 */
export default function PublicFormPage() {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, error: null, form: null });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [paymentLink, setPaymentLink] = useState(null);

  useEffect(() => {
    let cancelled = false;
    publicApi.get(`/forms/${id}/public`)
      .then(({ data }) => {
        if (!cancelled) setState({ loading: false, error: null, form: data.data });
        if (!cancelled) publicApi.post(`/forms/${id}/track`, { type: 'view' }).catch(() => {});
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            loading: false,
            error: err.response?.status === 403
              ? (err.response?.data?.message || 'This form is not accepting submissions right now.')
              : err.response?.status === 404
                ? 'This form is not available.'
                : 'Unable to load this form right now.',
            form: null,
          });
        }
      });
    return () => { cancelled = true; };
  }, [id]);

  const handlePageChange = (pageIndex) => {
    publicApi.post(`/forms/${id}/track`, { type: 'page_view', pageIndex }).catch(() => {});
  };

  const handleUploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await publicApi.post(`/forms/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.data;
  };

  const handleSubmit = async (values, antiSpamMeta) => {
    setSubmitting(true);
    try {
      const { data } = await publicApi.post(`/forms/${id}/submissions/public`, {
        values,
        source: {
          referrer: document.referrer,
          utmSource: new URLSearchParams(window.location.search).get('utm_source') || undefined,
          utmMedium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
          utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
        },
        meta: antiSpamMeta,
      });
      setPaymentLink(data.data?.paymentLink || null);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9] flex items-start justify-center py-10 px-4">
      <div className="w-full max-w-[640px] bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)] shadow-sm p-8">
        {state.loading && (
          <div className="flex items-center justify-center py-16 text-[#706E6B]">
            <Loader size={20} className="animate-spin mr-2" /> Loading form…
          </div>
        )}

        {state.error && (
          <div className="flex flex-col items-center py-16 text-center">
            <AlertCircle size={32} className="text-[#EA001E] mb-3" />
            <p className="text-[14px] text-[#181818]">{state.error}</p>
          </div>
        )}

        {!state.loading && !state.error && state.form && (
          <>
            <div className="mb-6">
              <h1 className="text-[20px] font-bold text-[#181818]">{state.form.name}</h1>
              {state.form.description && (
                <p className="text-[13px] text-[#706E6B] mt-1">{state.form.description}</p>
              )}
            </div>
            <FormRenderer
              schema={state.form.schema}
              antiSpam={state.form.antiSpam}
              onSubmit={handleSubmit}
              onUploadFile={handleUploadFile}
              onPageChange={handlePageChange}
              submitting={submitting}
              submitted={submitted}
              successMessage={state.form.schema?.settings?.successMessage}
              persistKey={`waapleads_form_draft_${id}`}
            />
            {submitted && paymentLink?.url && (
              <div className="mt-4 text-center">
                <a
                  href={safeHttpUrl(paymentLink.url)}
                  className="inline-block px-6 py-2.5 rounded-[4px] text-white text-[13px] font-bold bg-[#0176D3] hover:opacity-90 transition-opacity"
                >
                  Complete Payment
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
